import { useEffect, useRef, useState } from 'react'
import type { User, UserManager } from 'oidc-client-ts'
import { clearAuthTokenCookies, setAuthTokenToCookies } from 'api/auth'
import { reportClientError } from 'services/errorReportingService'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { ACCOUNT_INIT_COMPLETE } from 'actions/actionConstants'
import { recoverOidcSession } from './recoverOidcSession'
import { clearAuthMethodMarker, isOidcSession } from './authMethod'

// App-wide, render-nothing coordinator for a v2 (OIDC) session. Two jobs, both
// on a single long-lived UserManager:
//  1. Mirror the access token into the cookies the app reads for auth on every
//     userLoaded (initial load + each silent renew).
//  2. When SSR rendered anonymous because the access token had expired, restore
//     the session via the refresh token (see recoverOidcSession) so the page
//     doesn't stay logged out until a manual reload.
// Returns whether a restore is in flight so the app can show a blocking overlay.
// No-op for the v1 cookie-only path; imports oidc-client-ts lazily (./config) so
// it stays out of non-OIDC brands' bundles.
export default function useOidcSessionCoordinator(): boolean {
  const [isMounted, setIsMounted] = useState(false)
  const [isRestoringSession, setIsRestoringSession] = useState(false)
  const dispatch = useAppDispatch()
  // A ref so the coordinator effect can read login state without re-running (and
  // re-creating the UserManager) when it flips true after a restore.
  const loggedInRef = useRef(false)
  loggedInRef.current = useAppSelector(selectLoggedIn)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    // isOidcSession() reads storage, so only evaluate after mount.
    if (!isMounted || !isOidcSession()) {
      return
    }

    let userManager: UserManager | undefined
    let disposed = false

    const syncCookie = (user: User) => {
      if (user.access_token) {
        setAuthTokenToCookies(user.access_token)
      }
    }

    const onSilentRenewError = (error: Error) => {
      reportClientError(error, { context: 'oidcSilentRenew' }).catch(() => undefined)
    }

    import('./config')
      .then(async({ createOidcUserManager }) => {
        if (disposed) {
          return
        }
        userManager = createOidcUserManager()
        userManager.events.addUserLoaded(syncCookie)
        userManager.events.addSilentRenewError(onSilentRenewError)

        const user = await userManager.getUser()
        if (user) {
          syncCookie(user)
        }

        // SSR rendered anonymous but a v2 session is present, so the access token
        // expired in the background. Refresh it via this same manager (so its
        // silent-renew re-arms) and reconcile the UI to logged in.
        if (!loggedInRef.current) {
          setIsRestoringSession(true)
          try {
            await dispatch(recoverOidcSession(userManager))
          } catch(error) {
            // Refresh token also expired or revoked: the session is unrecoverable.
            // Clear the oidc-client user record + marker + stale cookies so the
            // next load settles as a normal logged-out user instead of re-attempting
            // this doomed restore forever (and the two stores stay aligned).
            reportClientError(error, { context: 'oidcSessionRecover' }).catch(() => undefined)
            await userManager.removeUser().catch(() => undefined)
            clearAuthMethodMarker()
            clearAuthTokenCookies()
            dispatch({ type: ACCOUNT_INIT_COMPLETE })
          } finally {
            if (!disposed) {
              setIsRestoringSession(false)
            }
          }
        }
      })
      .catch((error) => {
        // Lazy chunk or manager setup failed. initialiseUser defers init for the
        // OIDC path, so settle it here or the app hangs uninitialised (login modal
        // and promo suppressed, auth routes held on their loader).
        reportClientError(error, { context: 'oidcSessionCoordinator' }).catch(() => undefined)
        if (!disposed) {
          setIsRestoringSession(false)
        }
        dispatch({ type: ACCOUNT_INIT_COMPLETE })
      })

    return () => {
      disposed = true
      if (userManager) {
        userManager.events.removeUserLoaded(syncCookie)
        userManager.events.removeSilentRenewError(onSilentRenewError)
      }
    }
  }, [isMounted, dispatch])

  return isRestoringSession
}
